import { useCallback, useRef, useState } from 'react';

export type Area = {
  width: number;
  height: number;
  x: number;
  y: number;
};

interface UseVideoCropReturn {
  cropVideo: (file: File, cropArea: Area) => Promise<File>;
  cancelCrop: () => void;
  isProcessing: boolean;
  progress: number;
}

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }
  const types = [
    'video/webm;codecs=h264',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return '';
}

export function useVideoCrop(): UseVideoCropReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const cancelledRef = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const cropVideo = useCallback((file: File, cropArea: Area): Promise<File> => {
    setIsProcessing(true);
    setProgress(0);
    cancelledRef.current = false;

    return new Promise<File>((resolve, reject) => {
      const video = document.createElement('video');
      videoRef.current = video;
      const videoUrl = URL.createObjectURL(file);
      video.src = videoUrl;
      video.playsInline = true;
      video.preload = 'auto';

      const cleanup = () => {
        URL.revokeObjectURL(videoUrl);
        video.pause();
        video.removeAttribute('src');
        video.load();
        videoRef.current = null;
        recorderRef.current = null;
        audioCtxRef.current?.close();
        audioCtxRef.current = null;
      };

      video.onloadeddata = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 720;
        canvas.height = 1280;
        const ctx = canvas.getContext('2d')!;

        const canvasStream = canvas.captureStream(30);
        let combinedStream: MediaStream = canvasStream;
        let hasAudio = false;

        // Capture audio via Web Audio API
        // createMediaElementSource redirects audio away from speakers
        try {
          const audioCtx = new AudioContext();
          audioCtxRef.current = audioCtx;
          const source = audioCtx.createMediaElementSource(video);
          const dest = audioCtx.createMediaStreamDestination();
          source.connect(dest);
          if (dest.stream.getAudioTracks().length > 0) {
            combinedStream = new MediaStream([
              ...canvasStream.getVideoTracks(),
              ...dest.stream.getAudioTracks(),
            ]);
            hasAudio = true;
          }
        } catch {
          // Audio capture failed, proceed with video only
        }

        const mimeType = getSupportedMimeType();
        if (!mimeType) {
          cleanup();
          setIsProcessing(false);
          reject(new Error('Video recording is not supported in this browser'));
          return;
        }

        // Some browsers can't record audio+video with certain codecs.
        // Try with audio first, fall back to video-only.
        let recorder: MediaRecorder;
        try {
          recorder = new MediaRecorder(combinedStream, {
            mimeType,
            videoBitsPerSecond: 4_000_000,
          });
        } catch {
          if (hasAudio) {
            // Retry without audio tracks
            recorder = new MediaRecorder(canvasStream, {
              mimeType,
              videoBitsPerSecond: 4_000_000,
            });
          } else {
            cleanup();
            setIsProcessing(false);
            reject(new Error('Video recording failed to start'));
            return;
          }
        }
        recorderRef.current = recorder;

        const chunks: Blob[] = [];

        const onDataAvailable = (e: BlobEvent) => {
          if (e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        const onStop = () => {
          cleanup();

          if (cancelledRef.current) {
            setIsProcessing(false);
            setProgress(0);
            reject(new Error('Cancelled'));
            return;
          }

          const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
          const type = mimeType.includes('mp4') ? 'video/mp4' : 'video/webm';
          const blob = new Blob(chunks, { type });
          const croppedFile = new File(
            [blob],
            file.name.replace(/\.\w+$/, `.${ext}`),
            { type }
          );
          setProgress(100);
          setIsProcessing(false);
          resolve(croppedFile);
        };

        const onError = () => {
          cleanup();
          setIsProcessing(false);
          reject(new Error('Video recording failed'));
        };

        recorder.ondataavailable = onDataAvailable;
        recorder.onstop = onStop;
        recorder.onerror = onError;

        const duration = video.duration;

        // Firefox throws on .start() if the stream has audio tracks
        // the codec doesn't support. Fall back to video-only.
        try {
          recorder.start(100);
        } catch {
          if (hasAudio) {
            recorder = new MediaRecorder(canvasStream, {
              mimeType,
              videoBitsPerSecond: 4_000_000,
            });
            recorder.ondataavailable = onDataAvailable;
            recorder.onstop = onStop;
            recorder.onerror = onError;
            recorderRef.current = recorder;
            recorder.start(100);
          } else {
            cleanup();
            setIsProcessing(false);
            reject(new Error('Video recording failed to start'));
            return;
          }
        }

        // Play unmuted so AudioContext can capture audio
        video.muted = false;
        video.play().catch(() => {
          // Autoplay policy blocked unmuted playback, retry muted (no audio)
          video.muted = true;
          video.play();
        });

        setProgress(5);

        const drawFrame = () => {
          if (cancelledRef.current || video.ended) {
            if (recorder.state === 'recording') {
              recorder.stop();
            }
            return;
          }

          if (video.paused) {
            return;
          }

          ctx.drawImage(
            video,
            cropArea.x,
            cropArea.y,
            cropArea.width,
            cropArea.height,
            0,
            0,
            720,
            1280
          );

          const p = Math.min((video.currentTime / duration) * 90 + 5, 95);
          setProgress(Math.round(p));
          requestAnimationFrame(drawFrame);
        };

        requestAnimationFrame(drawFrame);

        video.onended = () => {
          ctx.drawImage(
            video,
            cropArea.x,
            cropArea.y,
            cropArea.width,
            cropArea.height,
            0,
            0,
            720,
            1280
          );
          if (recorder.state === 'recording') {
            recorder.stop();
          }
        };
      };

      video.onerror = () => {
        URL.revokeObjectURL(videoUrl);
        videoRef.current = null;
        setIsProcessing(false);
        reject(new Error('Failed to load video'));
      };
    });
  }, []);

  const cancelCrop = useCallback(() => {
    cancelledRef.current = true;
    videoRef.current?.pause();
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    } else {
      setIsProcessing(false);
      setProgress(0);
    }
  }, []);

  return { cropVideo, cancelCrop, isProcessing, progress };
}
